import React, { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import { Box, IconButton, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import EmojiPickerCustom from '../Emojis/EmojiPickerCustom';

interface Props {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  onEscapeKey?: () => void;
  onPaste?: (event: ClipboardEvent) => void;
  inputProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

interface MessageInputRef {
  focus: () => void;
  blur: () => void;
  setSelectionRange: (start: number, end: number) => void;
}

const MessageInput = styled(forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function MessageInputBase(props, ref) {
    return <textarea ref={ref} {...props} />;
  }
))(({ theme }) => ({
  ...theme.typography.subtitle2,
  border: 'none',
  outline: 'none',
  width: '100%',
  minHeight: 40,
  maxHeight: 120,
  padding: theme.spacing(1),
  paddingRight: theme.spacing(6),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  resize: 'none',
  lineHeight: 1.4,
  overflowY: 'auto',
  fontFamily: 'inherit',
  '&::placeholder': {
    color: theme.palette.text.disabled,
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
    backgroundColor: 'transparent',
    cursor: 'not-allowed',
  },
}));

export const MessageInputWithEmoji = React.forwardRef<MessageInputRef, Props>(({
  value,
  onChange,
  onKeyDown,
  disabled,
  placeholder,
  onEscapeKey,
  onPaste,
  inputProps,
}, ref) => {
  const { t } = useTranslation();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiBtnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const emojiShortcutListener = useRef<(e: KeyboardEvent) => void>();

  const handleInsertEmoji = useCallback((emoji: string) => {
    if (inputRef.current) {
      const input = inputRef.current;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const newValue = value.slice(0, start) + emoji + value.slice(end);
      onChange(newValue);
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      onChange(value + emoji);
    }
    setShowEmojiPicker(false);
  }, [value, onChange]);

  const handleInputFocus = useCallback(() => {
    emojiShortcutListener.current = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === '0') {
        e.preventDefault();
        setShowEmojiPicker(true);
      }
    };
    window.addEventListener('keydown', emojiShortcutListener.current);
  }, []);

  const handleInputBlur = useCallback(() => {
    if (emojiShortcutListener.current) {
      window.removeEventListener('keydown', emojiShortcutListener.current);
    }
  }, []);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.currentTarget.value);
  }, [onChange]);

  useEffect(() => {
    function handleEscKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (showEmojiPicker) {
          setShowEmojiPicker(false);
          e.stopPropagation();
        } else if (onEscapeKey) {
          onEscapeKey();
        }
      }
    }
    window.addEventListener('keydown', handleEscKey, true);
    return () => window.removeEventListener('keydown', handleEscKey, true);
  }, [showEmojiPicker, onEscapeKey]);

  const focusInput = useCallback(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  // Exponer el método focusInput para uso externo
  React.useImperativeHandle(ref, () => ({
    focus: focusInput,
    blur: () => inputRef.current?.blur() || (() => {}),
    setSelectionRange: (start: number, end: number) => inputRef.current?.setSelectionRange(start, end) || (() => {})
  }));

  // Manejar eventos de pegado
  useEffect(() => {
    const input = inputRef.current;
    if (input && onPaste) {
      const pasteHandler = (event: ClipboardEvent) => {
        onPaste(event);
      };
      
      input.addEventListener('paste', pasteHandler);
      return () => input.removeEventListener('paste', pasteHandler);
    }
  }, [onPaste]);

  return (
    <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'stretch' }}>
      <MessageInput
        ref={inputRef}
        {...inputProps}
        disabled={disabled}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        rows={1}
      />
      <IconButton
        ref={emojiBtnRef}
        onClick={() => setShowEmojiPicker((prev) => !prev)}
        sx={{ position: 'absolute', right: 4, bottom: 4 }}
        disabled={disabled}
      >
        <span role="img" aria-label="emoji">😊</span>
      </IconButton>
      {showEmojiPicker && (
        <EmojiPickerCustom
          onSelect={handleInsertEmoji}
          onClose={() => setShowEmojiPicker(false)}
          anchorRef={emojiBtnRef}
          t={t}
        />
      )}
    </Box>
  );
});

export default MessageInputWithEmoji; 
